/**
 * popupOverlay.js - Elevates SVG popups above HTML drag overlays
 *
 * Problem: HTML drag overlays render above SVG content, obscuring popups like
 * the performance guide. SVG can't have z-index above HTML.
 *
 * Solution: When a registered popup becomes visible, clone its SVG content
 * into an HTML-positioned container with high z-index, hiding the original.
 * When hidden, remove the overlay and restore the original.
 */

const OVERLAY_Z_INDEX = 2147483647; // Maximum z-index value - must be above everything
const activeOverlays = new Map(); // uid -> { container, svgGroup, ... }
const pendingCreation = new Set(); // Prevent race conditions during async creation

/**
 * Register a popup uid to be elevated when visible.
 * Call this during initialization for each popup that needs elevation.
 */
export function registerPopup(uid) {
  // Will be handled by the visibility observer
  if (!window._popupOverlayUids) {
    window._popupOverlayUids = new Set();
  }
  window._popupOverlayUids.add(uid);
}

/**
 * Create an HTML overlay for an SVG element, positioning it above drag overlays.
 * @param {SVGElement} svgGroup - The SVG group to elevate
 * @param {string} uid - Unique identifier for this popup
 */
export function createOverlay(svgGroup, uid) {
  if (activeOverlays.has(uid)) {
    return; // Already has overlay
  }

  const svg = svgGroup.ownerSVGElement;
  if (!svg) {
    console.warn(`[popupOverlay] No ownerSVGElement for uid=${uid}`);
    return;
  }

  // Temporarily show element if hidden to get accurate bbox/ctm
  const wasHidden = svgGroup.style.display === "none";
  if (wasHidden) svgGroup.style.display = "";

  // Get the group's bounding box (used for the cloned SVG's viewBox attribute)
  const bbox = svgGroup.getBBox();

  // Use getBoundingClientRect for viewport position: getScreenCTM ignores CSS
  // transforms on HTML ancestors (e.g. #scrollStage's translateX).
  const elRect = svgGroup.getBoundingClientRect();

  // data-anchor-uid: centre on viewport so panels always appear on-screen
  // regardless of where the anchor menu has been dragged or whether the
  // panel's own SVG element is currently scrolled off-screen.
  const anchorUid = svgGroup.dataset?.anchorUid;
  if (!anchorUid && !elRect.width && !elRect.height) {
    if (wasHidden) svgGroup.style.display = "none";
    return;
  }

  const screenWidth  = anchorUid ? (bbox.width  || 320) : elRect.width;
  const screenHeight = anchorUid ? (bbox.height || 240) : elRect.height;
  const screenX = anchorUid
    ? Math.round((window.innerWidth  - screenWidth)  / 2)
    : elRect.left;
  const screenY = anchorUid
    ? Math.round((window.innerHeight - screenHeight) / 2)
    : elRect.top;

  // Create container div with position:fixed to escape any stacking context
  const container = document.createElement("div");
  container.className = "popup-overlay-container";
  container.dataset.uid = uid;
  container.style.cssText = `
    position: fixed;
    left: ${screenX}px;
    top: ${screenY}px;
    width: ${screenWidth}px;
    height: ${screenHeight}px;
    z-index: ${OVERLAY_Z_INDEX};
    pointer-events: auto;
    overflow: visible;
  `;

  // Create an inline SVG that contains a clone of the group content
  const inlineSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  inlineSvg.setAttribute("width", screenWidth);
  inlineSvg.setAttribute("height", screenHeight);
  inlineSvg.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
  inlineSvg.style.cssText = `
    overflow: visible;
    display: block;
  `;

  // Clone the group content (deep clone)
  const clone = svgGroup.cloneNode(true);

  // Remove the transform from the clone since viewBox handles positioning
  clone.removeAttribute("transform");

  // Remove any id attributes to avoid duplicates (but keep data attributes)
  clone.querySelectorAll("[id]").forEach(el => {
    const id = el.getAttribute("id");
    // Keep functional ids but prefix them to avoid conflicts
    if (id.startsWith("ui(") || id.startsWith("button(")) {
      el.setAttribute("id", `overlay-${id}`);
    } else {
      el.removeAttribute("id");
    }
  });

  inlineSvg.appendChild(clone);
  container.appendChild(inlineSvg);
  document.body.appendChild(container);  // Append to body for fixed positioning

  // Hide the original SVG with opacity (not display:none) so toggle state detection
  // still works but we don't see the original during scroll
  svgGroup.style.opacity = "0";

  // Store reference for cleanup
  activeOverlays.set(uid, {
    container,
    svgGroup,
    reposition: () => repositionOverlay(uid)
  });

  // Set up click handlers for close button and other interactive elements
  setupOverlayInteractions(container, uid);

  // Listen for resize and scroll to reposition
  window.addEventListener("resize", activeOverlays.get(uid).reposition);

  // Listen for scroll on document (capture phase to catch all scroll events)
  const scrollHandler = activeOverlays.get(uid).reposition;
  document.addEventListener("scroll", scrollHandler, { passive: true, capture: true });
  activeOverlays.get(uid).scrollHandler = scrollHandler;
}

/**
 * Remove the HTML overlay. Original SVG visibility is managed by setElementVisibility.
 */
export function removeOverlay(uid) {
  const overlay = activeOverlays.get(uid);
  if (!overlay) return;

  // Remove resize listener
  window.removeEventListener("resize", overlay.reposition);

  // Remove scroll listener
  if (overlay.scrollHandler) {
    document.removeEventListener("scroll", overlay.scrollHandler, { capture: true });
  }

  // Remove the container
  overlay.container.remove();

  // Restore original SVG opacity
  if (overlay.svgGroup) {
    overlay.svgGroup.style.opacity = "";
  }

  activeOverlays.delete(uid);
}

/** Reposition every open popup: the SVG it shows has moved. */
export function repositionAllPopups() {
  for (const uid of activeOverlays.keys()) repositionOverlay(uid);
}

/**
 * Reposition an existing overlay (e.g., after resize or scroll).
 */
function repositionOverlay(uid) {
  const overlay = activeOverlays.get(uid);
  if (!overlay) return;

  const svgGroup = overlay.svgGroup;
  const container = overlay.container;

  const bbox = svgGroup.getBBox();
  const elRect = svgGroup.getBoundingClientRect();
  if (!elRect.width && !elRect.height) return;

  const screenX = elRect.left;
  const screenY = elRect.top;
  const screenWidth = elRect.width;
  const screenHeight = elRect.height;

  // Use viewport coordinates directly (position:fixed)
  container.style.left = `${screenX}px`;
  container.style.top = `${screenY}px`;
  container.style.width = `${screenWidth}px`;
  container.style.height = `${screenHeight}px`;

  // Update SVG viewBox
  const inlineSvg = container.querySelector("svg");
  if (inlineSvg) {
    inlineSvg.setAttribute("width", screenWidth);
    inlineSvg.setAttribute("height", screenHeight);
    inlineSvg.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
  }
}

/**
 * Set up click handlers for interactive elements within the overlay.
 */
function setupOverlayInteractions(container, uid) {
  // Handle close button clicks - directly hide via the visibility system
  container.querySelectorAll('[id*="action:toggle"], [data-oscilla*="action:toggle"]').forEach(el => {
    el.style.cursor = "pointer";
    el.addEventListener("click", async (e) => {
      e.stopPropagation();
      e.preventDefault();
      // Use the visibility system to hide and refresh toggle indicators
      const { hideElement } = await import('../cues/animShared.js');
      const { refreshToggleIndicatorsForUid } = await import('../cues/button.js');
      hideElement(uid);
      refreshToggleIndicatorsForUid(uid);
    });
  });

  // Make the container draggable (sync with original SVG group)
  let isDragging = false;
  let startX, startY, startLeft, startTop;

  container.addEventListener("pointerdown", (e) => {
    // Only drag from background, not interactive elements
    if (e.target.closest('[id*="action:toggle"], [data-oscilla*="action:toggle"]')) return;

    isDragging = true;
    startX = e.clientX;
    startY = e.clientY;
    startLeft = parseFloat(container.style.left);
    startTop = parseFloat(container.style.top);
    container.setPointerCapture(e.pointerId);
    container.style.cursor = "grabbing";
  });

  container.addEventListener("pointermove", (e) => {
    if (!isDragging) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    container.style.left = `${startLeft + dx}px`;
    container.style.top = `${startTop + dy}px`;
    // Sync original SVG position in real-time
    syncPositionToSvg(uid);
  });

  container.addEventListener("pointerup", (e) => {
    if (!isDragging) return;
    isDragging = false;
    container.releasePointerCapture(e.pointerId);
    container.style.cursor = "";

    // Sync position back to original SVG group
    syncPositionToSvg(uid);
  });
}

/**
 * Sync the overlay position back to the original SVG element.
 */
function syncPositionToSvg(uid) {
  const overlay = activeOverlays.get(uid);
  if (!overlay) return;

  const { container, svgGroup } = overlay;

  // The container is position:fixed, so left/top are already viewport coords
  // (as repositionOverlay writes them) — no parent offset to add.
  const currentLeft = parseFloat(container.style.left);
  const currentTop = parseFloat(container.style.top);

  // Get the SVG's CTM to convert screen coords back to SVG coords
  const svg = svgGroup.ownerSVGElement;
  const ctm = svg.getScreenCTM();
  if (!ctm) return;

  // Calculate new SVG position
  const screenX = currentLeft;
  const screenY = currentTop;

  const bbox = svgGroup.getBBox();
  // getScreenCTM ignores CSS transforms on HTML ancestors (e.g. #scrollStage's
  // translateX). Use the SVG's actual rect as the screen origin instead of ctm.e/f.
  const svgRect = svg.getBoundingClientRect();
  const svgX = (screenX - svgRect.left) / ctm.a - bbox.x;
  const svgY = (screenY - svgRect.top) / ctm.d - bbox.y;

  // Update the SVG group's transform
  svgGroup.setAttribute("transform", `translate(${svgX}, ${svgY})`);
}


/**
 * Toggle overlay state - create if visible, remove if hidden.
 * Called by the UI toggle system when registered popups change visibility.
 */
export async function toggleOverlay(uid, isVisible) {
  if (isVisible) {
    // Prevent duplicate creation during async operation
    if (activeOverlays.has(uid) || pendingCreation.has(uid)) {
      return;
    }
    pendingCreation.add(uid);

    try {
      const { findElementByUid } = await import('../cues/animShared.js');
      const found = findElementByUid(uid);

      if (found?.el && !activeOverlays.has(uid)) {
        // Check if this element has an associated drag-html overlay
        const hasDragOverlay = Array.from(document.querySelectorAll('.oscilla-drag-overlay')).some(
          overlay => overlay._svgElement === found.el
        );

        // Exclude panels containing interactive elements that won't work when cloned:
        // - o2p elements (faders/mixers)
        // - ui() elements that are NOT close buttons (we handle action:toggle specially)
        const hasO2pElements = found.el.querySelector('[id*="o2p("], [data-oscilla*="o2p("]') !== null ||
                               found.el.querySelector('[id*="group:"], [data-oscilla*="group:"]') !== null;

        // Check for ui() elements that are NOT just a single close button
        // A close button is typically the only ui() element and has 'action:toggle'
        // Visibility panels have MULTIPLE toggle buttons - exclude those
        const uiElements = Array.from(found.el.querySelectorAll('[id*="ui("], [data-oscilla*="ui("]'));
        const hasMultipleUiElements = uiElements.length > 1;
        const hasNonToggleUiElements = uiElements.some(el => {
          const id = el.id || '';
          return !id.includes('action:toggle');
        });
        const hasInteractiveUiElements = hasMultipleUiElements || hasNonToggleUiElements;

        const isSimplePanel = hasDragOverlay && !hasO2pElements && !hasInteractiveUiElements;

        if (isSimplePanel) {
          if (!window._popupOverlayUids?.has(uid)) {
            registerPopup(uid);
          }
          createOverlay(found.el, uid);
        }
      }
    } finally {
      pendingCreation.delete(uid);
    }
  } else {
    // Remove overlay if it exists
    if (activeOverlays.has(uid)) {
      removeOverlay(uid);
    }
  }
}

/**
 * Scan for draggable popup panels and register them.
 */
function scanAndRegisterPopups() {
  const allElements = document.querySelectorAll('[id*="drag-html:1"], [data-oscilla*="drag-html:1"]');
  allElements.forEach(el => {
    const id = el.id || "";
    // Extract uid from id like "drag(1, uid:performanceGuide, drag-html:1)"
    const match = id.match(/uid:([^,\s\)]+)/);
    if (match && !window._popupOverlayUids?.has(match[1])) {
      registerPopup(match[1]);
    }
  });
}

/**
 * Initialize the popup overlay system.
 * Auto-detects draggable popup panels (drag-html:1) and registers them.
 */
export function initPopupOverlays() {
  // Scan now if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanAndRegisterPopups);
  } else {
    // DOM might not have SVG yet, delay slightly
    setTimeout(scanAndRegisterPopups, 100);
    // Also re-scan after cues are likely processed
    setTimeout(scanAndRegisterPopups, 1000);
  }
}

// Export for manual re-scan if needed
export { scanAndRegisterPopups as rescanPopups };

// Auto-initialize when module loads
if (typeof window !== "undefined") {
  initPopupOverlays();
}
